import { BaseLayerPicker, DitapMask } from "@cesium/widgets";

/**
 * Ditap 위젯 전용 BaseLayerPicker 상속 클래스.</br>
 * BaseLayerPicker Inheritance Class for Ditap Widget.
 * @extends BaseLayerPicker
 *
 * @alias DitapBaseLayerPicker
 * @constructor
 *
 * @param {Element|string} container 부모 HTML 컨테이너 노드 또는 ID.</br>The parent HTML container node or ID for this widget.
 * @param {object} options 속성 객체.</br>Object with the following properties:
 * @param {Globe} options.globe 사용할 Globe.</br>The Globe to use.
 * @param {ProviderViewModel[]} [options.imageryProviderViewModels=[]]  사용할 ProviderViewModel 인스턴스 배열.</br>The array of ProviderViewModel instances to use for imagery.
 * @param {ProviderViewModel[]} [options.terrainProviderViewModels=[]] 지형에 사용할 ProviderViewModel 인스턴스 배열.</br>The array of ProviderViewModel instances to use for terrain.
 * @param {DitapViewer} viewer 사용할 DitapViewer 인스턴스</br>The DitapViewer instance to use.
 * 
 * @example
 * 
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 * 
 * const container = viewer.container;  
 * const toolbar = container.getElementsByClassName(
 *   "js-ditap-toolbal-top-btn1"
 *  )[0];
  
 * const imageryProviderViewModels = viewer.createDefaultImageryProviderViewModels();
 * const terrainProviderViewModels = viewer.createDefaultTerrainProviderViewModels();
 * 
 * new Ditap.DitapBaseLayerPicker(
 *      toolbar,
 *      {
          globe: viewer.cesiumWidget.scene.globe,
 *        imageryProviderViewModels: imageryProviderViewModels,
 *        terrainProviderViewModels: terrainProviderViewModels,
 *      },
 *      viewer
 *    );
 * 
 */
class DitapBaseLayerPicker extends BaseLayerPicker {
  constructor(container, options, viewer) {
    super(container, options);

    this.baseLayerPickerButton = undefined;

    // 배경 지도 버튼 생성
    this.createBaseMapBtn(container, viewer);

    // 배경 지도 팝업 생성
    this.createBaseMapPopup(container, viewer);

    // 팝업에 IMAGERY 리스트 추가
    this.addImageryList(options);

    // 팝업에 TERRAIN 리스트 추가
    this.addTerrainList(options);

    // 배경지도 팝업 여닫기 클릭 이벤트 생성
    this.createPopupEvent(viewer);

    // IMAGERY 리스트 클릭 이벤트 생성
    this.createImageryClickEvent(options);

    // TERRAIN 리스트 클릭 이벤트 생성
    this.createTerrainClickEvent(options);

    // 지형 버튼 클릭 이벤트 생성
    // this.createTerrainEvent();

    // 탭 클릭 이벤트
    this.addTabClickEvent();

    this.viewer = viewer;

    super.destroy();
  }

  /*
   * 베이스 레이어 맵 버튼 생성.</br>Create BaseLyaer Map Button
   * @param {Element} container 부모 HTML 컨테이너 요소.</br>The parent HTML container
   */
  createBaseMapBtn(container, viewer) {
    const mobileContainer = viewer.mobileBtnContainer;
    const currentWidth = window.innerWidth;

    const baseLayerPickerButton = document.createElement("button");
    baseLayerPickerButton.className = "ditap-basemap-btn";

    const li = document.createElement("li");
    li.classList.add("ditap-func-btn-item");

    if (currentWidth <= 600) {
      li.appendChild(baseLayerPickerButton);
      mobileContainer.querySelector(".js-position-basemap").appendChild(li);
      baseLayerPickerButton.classList.add("ditap-mobile-btn");
    } else {
      baseLayerPickerButton.classList.add("ditap-toolbar-btn");
      container.appendChild(baseLayerPickerButton);
    }

    const p1 = document.createElement("p");
    const p2 = document.createElement("p");
    p1.classList.add("ditap-function-btn-img", "js-basemap-btn");
    p2.className = "ditap-btn-tooltip";
    p2.textContent = "지도 선택";

    baseLayerPickerButton.appendChild(p1);
    baseLayerPickerButton.appendChild(p2);

    this.baseLayerPickerButton = baseLayerPickerButton;

    this.mapImg = p1;

    const resizeOriginal = () => {
      // 윈도우의 현재 너비를 확인
      const currentWidth = window.innerWidth;
      if (currentWidth <= 600) {
        li.appendChild(baseLayerPickerButton);
        // mobileContainer.appendChild(li);
        mobileContainer.querySelector(".js-position-basemap").appendChild(li);
        baseLayerPickerButton.classList.add("ditap-mobile-btn");
        baseLayerPickerButton.classList.remove("ditap-toolbar-btn");
      } else {
        baseLayerPickerButton.style.display = "flex";
        baseLayerPickerButton.classList.remove("ditap-mobile-btn");
        baseLayerPickerButton.classList.add("ditap-toolbar-btn");
        container.appendChild(baseLayerPickerButton);
      }
    };
    this._resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this._resizeEvent);
  }

  /**
   * 위젯 파괴.
   * Destroys the widget.
   */
  destroy() {
    if (this._resizeEvent) {
      window.removeEventListener("resize", this._resizeEvent);
    }

    this._resizeEvent = undefined;
    this.baseLayerPickerButton = undefined;
    this.viewer = undefined;
    this.baseLayerPickerButton = undefined;
    this.mapImg = undefined;
    this.imageryBox = undefined;
    this.terrainBox = undefined;
    this.mapPopup = undefined;
    this.closeBtn = undefined;
    this.mapTap = undefined;
    this.terrainTap = undefined;
    this.mask = undefined;
    this.terrainElements = undefined;
    this.imageryElements = undefined;
    this._closeDropDown = undefined;
    this._container = undefined;
    this._dropPanel = undefined;
    this._element = undefined;
    this._viewModel = undefined;
  }

  /*
   * 베이스 레이어 팝업 버튼 생성.</br> Create BaseLyaer Map Pop-up
   * @param {Element} container 부모 HTML 컨테이너 요소.</br>The parent HTML container
   */
  createBaseMapPopup(container, viewer) {
    const div1 = document.createElement("div");
    const div2 = document.createElement("div");
    const h2 = document.createElement("h2");
    const button = document.createElement("button");
    const imageryBox = document.createElement("div");
    const terrainBox = document.createElement("div");

    div1.classList.add(
      "ditap-card",
      "js-basemap-card",
      "ditap-map-mobile",
      "ditap-card-mobile",
      "ditap-basemap-popup"
    );
    div1.classList.add("hide");
    div2.classList.add("ditap-card-map");
    h2.classList.add("ditap-card-title");
    button.classList.add("ditap-popup-close-btn", "ditap-custom-button");
    // div1.style.paddingBottom = "0px";
    h2.textContent = "지도 선택";
    imageryBox.classList.add("ditap-card-content", "js-ditap-imagery-card");
    terrainBox.classList.add(
      "ditap-card-content",
      "js-ditap-terrain-card",
      "hide"
    );

    const div3 = document.createElement("div");
    const div4 = document.createElement("button");
    const div5 = document.createElement("button");

    div3.classList.add("ditap-map-tap-wrap");
    div4.classList.add("ditap-map-tap", "on");
    div5.classList.add("ditap-map-tap");

    div4.textContent = "지도";
    div5.textContent = "지형";

    div4.classList.add("ditap-custom-button");
    div5.classList.add("ditap-custom-button");

    div3.append(div4);
    div3.append(div5);

    div2.append(h2);
    div2.append(button);
    div1.append(div2);
    div1.append(div3);
    div1.append(imageryBox);
    div1.append(terrainBox);

    this.imageryBox = imageryBox;
    this.terrainBox = terrainBox;
    this.mapPopup = div1;
    this.closeBtn = button;
    // this.toggleBtn = toggle;

    this.mapTap = div4;
    this.terrainTap = div5;
    this.mask = new DitapMask();

    viewer.container.querySelector(".cesium-viewer").append(this.mapPopup);
  }

  addTabClickEvent() {
    this.mapTap.addEventListener(
      "click",
      (this.mapTabClickEvent = () => {
        this.terrainTap.classList.remove("on");
        this.mapTap.classList.add("on");

        // 지도 box 활성화
        this.imageryBox.classList.remove("hide");
        this.terrainBox.classList.add("hide");
      })
    );

    this.terrainTap.addEventListener(
      "click",
      (this.terrainTabClickEvent = () => {
        this.mapTap.classList.remove("on");
        this.terrainTap.classList.add("on");

        // 터레인 box활성화
        this.terrainBox.classList.remove("hide");
        this.imageryBox.classList.add("hide");
      })
    );
  }

  /*
   * 베이스 레이어 팝업에 이미지 HTML 요소 추가.</br>
   * Adding a Imagery HTML element to the BaseLayer Pop-up
   * @param {Element} container 부모 HTML 컨테이너 요소.
   * </br>The parent HTML container.
   * @param {object} options 속성 객체.</br>Object with the following properties:
   * @param {ProviderViewModel[]} [options.imageryProviderViewModels=[]]  사용할 ProviderViewModel 인스턴스 배열.</br>The array of ProviderViewModel instances to use for imagery.
   */

  addImageryList(options) {
    let ul1;
    const imageryElements = [];

    if (options.imageryProviderViewModels.length >= 0) {
      ul1 = document.createElement("ul");
      ul1.classList.add("ditap-card-list");
    }

    options.imageryProviderViewModels.forEach((imageryProvider, index) => {
      const li1 = document.createElement("button");
      const p3 = document.createElement("p");
      const p4 = document.createElement("p");

      li1.classList.add("ditap-card-item");
      li1.classList.add("btn-ditap-map");
      p3.classList.add("ditap-basemap-img");
      p3.style.backgroundImage = `url(${imageryProvider.iconUrl.toString()})`;
      p4.classList.add("ditap-basemap-title");
      p4.textContent = imageryProvider.name;

      ul1.appendChild(li1);
      li1.appendChild(p3);
      li1.appendChild(p4);
      li1.classList.add("ditap-custom-button");
      imageryElements.push(li1);
    });

    this.imageryBox.appendChild(ul1);

    this.imageryElements = imageryElements;
  }

  /*
   * 베이스 레이어 팝업에 터레인 HTML 요소 추가.</br>
   * Adding a Imagery HTML element to the BaseLayer Pop-up.
   * @param {Element} container 부모 HTML 컨테이너 요소.
   * </br>The parent HTML container.
   * @param {object} options 속성 객체.</br>Object with the following properties:
   * @param {ProviderViewModel[]} [options.terrainProviderViewModels=[]] 지형에 사용할 ProviderViewModel 인스턴스 배열.</br>The array of ProviderViewModel instances to use for terrain.
   */
  addTerrainList(options) {
    let ul1;
    const terrainElements = [];

    if (options.terrainProviderViewModels.length >= 0) {
      ul1 = document.createElement("ul");
      ul1.classList.add("ditap-card-list");
    }

    options.terrainProviderViewModels.forEach((terrainProvider, index) => {
      const li1 = document.createElement("button");
      const p3 = document.createElement("p");
      const p4 = document.createElement("p");

      li1.classList.add("ditap-card-item");
      li1.classList.add("btn-ditap-map");
      p3.classList.add("ditap-basemap-img");
      p3.style.backgroundImage = `url(${terrainProvider.iconUrl.toString()})`;
      p4.classList.add("ditap-basemap-title");
      p4.textContent = terrainProvider.name;

      ul1.appendChild(li1);
      li1.appendChild(p3);
      li1.appendChild(p4);
      li1.classList.add("ditap-custom-button");

      terrainElements.push(li1);
    });

    while (ul1.getElementsByTagName("li").length % 3 !== 0) {
      const li1 = document.createElement("li");
      li1.classList.add("ditap-card-item");
      li1.classList.add("btn-ditap-map");
      ul1.appendChild(li1);
    }

    this.terrainBox.appendChild(ul1);

    this.terrainElements = terrainElements;
  }

  /*
   * baseLayerPickerButton 클릭 시 실행 이벤트 생성.</br>
   * Create a click event that triggers when the baseLayerPickerButton is clicked
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createPopupEvent(viewer) {
    const that = this;
    this.baseLayerPickerButton.addEventListener("click", () => {
      viewer.clickToolbar(that);
    });

    // 헤더 닫기 버튼
    this.closeBtn.addEventListener("click", () => {
      viewer.clickToolbar(that);
    });
  }

  /**
   * 베이스 레이어 팝업 열기.</br>
   * Open BaseLayer Popup.
   */
  open() {
    this.mask.openMobile();
    this.mapPopup.classList.remove("hide");
    this.mapImg.classList.add("on");

    // 현재 선택된 Imagery element에 선택클래스 추가하기
    const imageryElements = this.imageryElements;
    for (let i = 0; i < imageryElements.length; i++) {
      if (
        imageryElements[i].getElementsByClassName("ditap-basemap-title")[0]
          .textContent === this.viewModel.selectedImagery.name
      ) {
        imageryElements[i]
          .getElementsByClassName("ditap-basemap-img")[0]
          .classList.add("on");
      } else if (
        imageryElements[i]
          .getElementsByClassName("ditap-basemap-img")[0]
          .classList.contains("on")
      ) {
        imageryElements[i]
          .getElementsByClassName("ditap-basemap-img")[0]
          .classList.remove("on");
      }
    }

    // 현재 선택된 Terrain element에 선택클래스 추가하기
    const terrainElements = this.terrainElements;
    for (let i = 0; i < terrainElements.length; i++) {
      if (
        terrainElements[i].getElementsByClassName("ditap-basemap-title")[0]
          .textContent === this.viewModel.selectedTerrain.name
      ) {
        terrainElements[i]
          .getElementsByClassName("ditap-basemap-img")[0]
          .classList.add("on");
      } else if (
        terrainElements[i]
          .getElementsByClassName("ditap-basemap-img")[0]
          .classList.contains("on")
      ) {
        terrainElements[i]
          .getElementsByClassName("ditap-basemap-img")[0]
          .classList.remove("on");
      }
    }
  }

  /**
   * 베이스 레이어 팝업 닫기.</br>
   * Close BaseLayer Popup.
   */
  close() {
    this.mask.closeMobile();
    this.mapPopup.classList.add("hide");
    this.mapImg.classList.remove("on");
  }

  /*
   * 이미지 HTML 요소에 이벤트 리스너 추가.</br>
   * Add EventListener to Imagery HTMLelement.
   * @param {object} options 속성 객체.</br>Object with the following properties:
   * @param {ProviderViewModel[]} [options.imageryProviderViewModels=[]]  사용할 ProviderViewModel 인스턴스 배열.</br>The array of ProviderViewModel instances to use for imagery.
   * */
  createImageryClickEvent(options) {
    const baseMaps = this.imageryElements;
    for (let i = 0; i < baseMaps.length; i++) {
      const baseMap = baseMaps[i];

      baseMap.addEventListener("click", () => {
        this.mask.closeMobile();
        options.imageryProviderViewModels.forEach((imageryProvider) => {
          if (
            imageryProvider.name ===
            baseMap.getElementsByClassName("ditap-basemap-title")[0].textContent
          ) {
            this.viewModel.selectedImagery = imageryProvider;
            this.mapPopup.classList.add("hide");
            this.mapImg.classList.remove("on");
          }
        });
      });
    }
  }

  /*
   * 터레인 HTML 요소에 이벤트 리스너 추가.</br>
   * Add EventListener to Terrain HTMLelement.
   * @param {object} options 속성 객체.</br>Object with the following.
   * @param {ProviderViewModel[]} [options.terrainProviderViewModels=[]] 지형에 사용할 ProviderViewModel 인스턴스 배열.</br>The array of ProviderViewModel instances to use for terrain.
   * */
  createTerrainClickEvent(options) {
    const baseMaps = this.terrainElements;
    for (let i = 0; i < baseMaps.length; i++) {
      const baseMap = baseMaps[i];

      baseMap.addEventListener("click", () => {
        this.mask.closeMobile();
        options.terrainProviderViewModels.forEach((terrainProvider) => {
          if (
            terrainProvider.name ===
            baseMap.getElementsByClassName("ditap-basemap-title")[0].textContent
          ) {
            this.viewModel.selectedTerrain = terrainProvider;
            this.mapPopup.classList.add("hide");
            this.mapImg.classList.remove("on");
          }
        });
      });
    }
  }
}

export default DitapBaseLayerPicker;
